/**
 * Modal for searching messages within a channel.
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  Input,
  Box,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Avatar,
  CircularProgress,
  Divider,
} from '@mui/joy';
import { MagnifyingGlass as SearchIcon } from '@phosphor-icons/react';
import { useSearchMessages } from '../../hooks/useChat';
import type { Message } from '../../types/chat';

// Get first and last initials from a name (e.g., "Jeffrey Stilwell" -> "JS")
const getInitials = (name: string | undefined): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0][0]?.toUpperCase() || '?';
  }
  const first = parts[0][0]?.toUpperCase() || '';
  const last = parts[parts.length - 1][0]?.toUpperCase() || '';
  return first + last;
};

// Generate a consistent color based on a string (name/username)
const AVATAR_COLORS = [
  '#7C3AED', // violet
  '#2563EB', // blue
  '#0891B2', // cyan
  '#059669', // emerald
  '#D97706', // amber
  '#DC2626', // red
  '#DB2777', // pink
  '#7C3AED', // purple
];

const getAvatarColor = (name: string | undefined): string => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

interface ChannelSearchModalProps {
  open: boolean;
  onClose: () => void;
  channelId: string;
  channelName?: string;
  onMessageSelect?: (message: Message) => void;
}

const ChannelSearchModal: React.FC<ChannelSearchModalProps> = ({
  open,
  onClose,
  channelId,
  channelName,
  onMessageSelect,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset when modal opens/closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setDebouncedQuery('');
    }
  }, [open]);

  const { data: searchResults, isLoading } = useSearchMessages(
    channelId,
    debouncedQuery
  );

  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Highlight matching text in content
  const highlightMatch = (content: string, query: string) => {
    if (!query.trim()) return content;

    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = content.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <Box
          key={index}
          component="span"
          sx={{
            bgcolor: 'warning.softBg',
            color: 'warning.softColor',
            px: 0.25,
            borderRadius: 'xs',
          }}
        >
          {part}
        </Box>
      ) : (
        part
      )
    );
  };

  const handleMessageClick = (message: Message) => {
    onMessageSelect?.(message);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        sx={{
          width: '100%',
          maxWidth: 600,
          maxHeight: '80vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ModalClose />
        <Typography level="title-lg" sx={{ mb: 2 }}>
          Search in {channelName ? `#${channelName}` : 'channel'}
        </Typography>

        {/* Search Input */}
        <Input
          autoFocus
          placeholder="Search messages..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          startDecorator={<SearchIcon />}
          endDecorator={isLoading ? <CircularProgress size="sm" /> : null}
          sx={{ mb: 2 }}
        />

        {/* Results */}
        <Box sx={{ flex: 1, overflow: 'auto', minHeight: 200 }}>
          {debouncedQuery.length < 2 ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'text.tertiary',
              }}
            >
              <Typography level="body-sm">
                Type at least 2 characters to search
              </Typography>
            </Box>
          ) : isLoading ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <CircularProgress />
            </Box>
          ) : searchResults?.results.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'text.tertiary',
              }}
            >
              <Typography level="body-sm">
                No messages found for "{debouncedQuery}"
              </Typography>
            </Box>
          ) : (
            <>
              <Typography level="body-xs" color="neutral" sx={{ mb: 1 }}>
                {searchResults?.total} result{searchResults?.total !== 1 ? 's' : ''} found
              </Typography>
              <List sx={{ '--ListItem-paddingY': '12px' }}>
                {searchResults?.results.map((message, index) => (
                  <React.Fragment key={message.id}>
                    <ListItem
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 'sm',
                        '&:hover': { bgcolor: 'background.level1' },
                      }}
                      onClick={() => handleMessageClick(message)}
                    >
                      <ListItemDecorator sx={{ alignSelf: 'flex-start', mt: 0.5 }}>
                        <Avatar
                          size="sm"
                          sx={{
                            bgcolor: getAvatarColor(message.sender?.full_name || message.sender?.username),
                            color: 'white',
                          }}
                        >
                          {getInitials(message.sender?.full_name || message.sender?.username)}
                        </Avatar>
                      </ListItemDecorator>
                      <ListItemContent>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: 1,
                            mb: 0.5,
                          }}
                        >
                          <Typography level="title-sm">
                            {message.sender?.full_name || message.sender?.username}
                          </Typography>
                          <Typography level="body-xs" color="neutral">
                            {formatTime(message.created_at)}
                          </Typography>
                        </Box>
                        <Typography
                          level="body-sm"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {highlightMatch(message.content, debouncedQuery)}
                        </Typography>
                      </ListItemContent>
                    </ListItem>
                    {index < (searchResults?.results.length || 0) - 1 && (
                      <Divider sx={{ my: 0.5 }} />
                    )}
                  </React.Fragment>
                ))}
              </List>
            </>
          )}
        </Box>
      </ModalDialog>
    </Modal>
  );
};

export default ChannelSearchModal;
